import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { socialService } from '../services/api'
import { useNotifications } from '../contexts/NotificationContext'
import { useAuth } from '../contexts/AuthContext'
import { useCartsQuery } from '../hooks/useCartsQuery'
import Lottie from './Lottie'
import ConversationCartPicker from './ConversationCartPicker'
import SharedCartAttachment from './SharedCartAttachment'
import './CreatePost.css'

function CreatePost({ onPostCreated }) {
  const [content, setContent] = useState('')
  const [selectedCart, setSelectedCart] = useState(null)
  const [showCartSelector, setShowCartSelector] = useState(false)
  const [composerFocused, setComposerFocused] = useState(false)
  const { addNotification } = useNotifications()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [sendAnimation, setSendAnimation] = useState(null)
  const sendLottieRef = useRef(null)
  const cartButtonRef = useRef(null)

  useEffect(() => {
    fetch('/Send.json')
      .then((res) => res.json())
      .then(setSendAnimation)
      .catch((err) => console.error('Erreur chargement animation Send:', err))
  }, [])

  const { data: cartsData } = useCartsQuery()
  const carts = cartsData ?? []

  const createPostMutation = useMutation({
    mutationFn: (data) => socialService.createPost(data.content, data.sharedCartId),
    onSuccess: () => {
      setContent('')
      setSelectedCart(null)
      queryClient.invalidateQueries(['social-feed'])
      if (user?.id) {
        queryClient.invalidateQueries(['social-user-posts', user.id])
      }
      if (onPostCreated) onPostCreated()
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Publication impossible',
        message: 'Erreur lors de la publication du post',
      })
      console.error('Erreur création post:', error)
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!content.trim()) {
      addNotification({
        type: 'error',
        title: 'Publication impossible',
        message: 'Le contenu du post ne peut pas être vide',
      })
      return
    }
    if (sendLottieRef.current) {
      sendLottieRef.current.goToAndPlay(0)
    }
    createPostMutation.mutate({
      content: content.trim(),
      sharedCartId: selectedCart?.id || null,
    })
  }

  const composing = composerFocused || Boolean(content.trim()) || showCartSelector || Boolean(selectedCart)

  return (
    <div className={`create-post-widget${composing ? ' is-composing' : ''}`}>
      <form onSubmit={handleSubmit} className="create-post-form">
        <textarea
          className="post-textarea"
          placeholder="Quoi de neuf ? Partagez vos pensées..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onFocus={() => setComposerFocused(true)}
          onBlur={() => setComposerFocused(false)}
          rows={4}
          maxLength={500}
        />
        {selectedCart ? (
          <div className="create-post-attachment">
            <SharedCartAttachment
              cart={selectedCart}
              sharedCartId={selectedCart.id}
              variant="feed"
              playKey={`draft:${selectedCart.id}`}
            />
          </div>
        ) : null}
        <div className="post-footer">
          <div className="post-actions">
            <button
              ref={cartButtonRef}
              type="button"
              className="action-btn"
              onClick={() => setShowCartSelector((open) => !open)}
              title="Partager un panier"
              aria-expanded={showCartSelector}
              aria-haspopup="listbox"
            >
              <span aria-hidden="true">🛒</span>
              <span>{selectedCart ? 'Changer le panier' : 'Partager un panier'}</span>
            </button>
            {selectedCart ? (
              <button type="button" className="action-btn" onClick={() => setSelectedCart(null)}>
                Retirer
              </button>
            ) : null}
          </div>
          <div className="post-submit">
            <span className="char-count">{content.length}/500</span>
            <button
              type="submit"
              className="submit-btn"
              disabled={!content.trim() || createPostMutation.isLoading}
            >
              {createPostMutation.isLoading ? (
                'Publication...'
              ) : sendAnimation ? (
                <Lottie
                  lottieRef={sendLottieRef}
                  animationData={sendAnimation}
                  loop={false}
                  autoplay={false}
                  style={{ width: 28, height: 28 }}
                />
              ) : (
                'Publier'
              )}
            </button>
          </div>
        </div>
      </form>
      <ConversationCartPicker
        isOpen={showCartSelector}
        anchor={cartButtonRef.current}
        carts={carts}
        onClose={() => setShowCartSelector(false)}
        onSelect={(cart) => {
          setSelectedCart(cart)
          setShowCartSelector(false)
        }}
      />
    </div>
  )
}

export default CreatePost
